import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { signalingBreakoutUserLeft } from 'features/breakout-rooms/actions';
import { signalingRoomJoined } from 'features/room/actions';
import { authorizeUser } from 'features/room/thunks/authorizeUser';
import { signalingUserJoined, signalingUserLeft } from 'features/users/actions';
import { RootState } from 'store/store';
import { PublishingFeedStatus, PublishingKind } from 'utils/webrtc/types';
import {
  activeSpeakerChanged,
  publisherJoined,
  publisherJoinedWithoutBroadcast,
  signalingAspectRatioAdjusted,
  speakerStoppedTalking,
} from 'features/streaming/actions';
import invert from 'lodash.invert';
import { UserId } from 'features/users/types';
import { phoneStreamDeleted } from 'features/sip/actions';
import { defaultHDBitrate } from 'utils/webrtc/environment';
import { safeLocalStorage } from 'utils/storage';
import {
  BlockedMediaPlaybackState,
  FeedId,
  GeneralStreamingUIState,
  LocalFeedUIState,
  LocalFeedUpdated,
  MediaStreamType,
  RemoteFeedUIState,
  RemoteFeedUpdated,
  StreamingState,
} from './types';
import { PRESERVE_ASPECT_RATIO_STORAGE_KEY } from './constants';

const getStoredPreserveAspectRatio = () =>
  safeLocalStorage.getItem(PRESERVE_ASPECT_RATIO_STORAGE_KEY) === 'true';

const hasStoredPreserveAspectRatio = () =>
  safeLocalStorage.getItem(PRESERVE_ASPECT_RATIO_STORAGE_KEY) !== null;

const getStoredMirrorVideo = () => {
  const stored = safeLocalStorage.getItem('mirror_video');
  return stored === null ? true : stored === 'true';
};

// @TODO split into publishing and screen-sharing states
export const getLocalFeedInitialState: () => LocalFeedUIState = () => ({
  status: PublishingFeedStatus.idle,
  hdVideo: {
    enabled: false,
    bitrate: defaultHDBitrate,
  },
  preserveAspectRatio: getStoredPreserveAspectRatio(),
  mirrorVideo: getStoredMirrorVideo(),
  audioEnabled: false,
  videoEnabled: false,
  audioBroadcasting: false,
  videoBroadcasting: false,
  audioProcessing: false,
  videoProcessing: false,
  streams: [],
  publisherJoined: false,
});

export const remoteFeedInitialState: RemoteFeedUIState = {
  feedIdByMid: {},
  streamsByFeedId: {},
};

export const blockedMediaPlaybackInitialState: BlockedMediaPlaybackState = {
  audio: false,
  mainDocumentVideo: false,
  pipDocumentVideo: false,
};

export const initialState: StreamingState = {
  activeSpeakerId: '',
  userIdByFeedId: {},
  userIdByScreensharingFeedId: {},
  feedIdByUserId: {},
  broadcastingByUserId: {},
  screensharingByUserId: {},
  preserveAspectRatioByUserId: {},
  publishingFeed: { ...getLocalFeedInitialState() },
  screensharingFeed: { ...getLocalFeedInitialState() },
  remoteFeed: remoteFeedInitialState,
  mediaPlaybackBlocked: { ...blockedMediaPlaybackInitialState },
};

export const streamingSlice = createSlice({
  name: 'streaming',
  initialState,
  reducers: {
    remoteFeedConnected(state, action: PayloadAction<RemoteFeedUpdated>) {
      state.remoteFeed = action.payload;
    },
    localFeedDisconnected(state, action: PayloadAction<{ kind: PublishingKind }>) {
      state[`${action.payload.kind}Feed`] = getLocalFeedInitialState();
    },
    localFeedUpdated(
      state,
      action: PayloadAction<{ kind: PublishingKind; data: LocalFeedUpdated }>
    ) {
      Object.assign(state[`${action.payload.kind}Feed`], action.payload.data);
    },
    generalDataUpdated(state, action: PayloadAction<GeneralStreamingUIState>) {
      state.userIdByFeedId = action.payload.userIdByFeedId;
      // Kept out of the invert below: a user with camera + screenshare would
      // make feedIdByUserId ambiguous.
      state.userIdByScreensharingFeedId = action.payload.userIdByScreensharingFeedId;
      state.feedIdByUserId = invert(action.payload.userIdByFeedId);
      state.broadcastingByUserId = action.payload.broadcastingByUserId;
      state.screensharingByUserId = action.payload.screensharingByUserId;
    },
    remoteFeedUpdated(state, action: PayloadAction<RemoteFeedUpdated>) {
      state.remoteFeed = action.payload;
    },
    remoteFeedDisconnected(state) {
      state.remoteFeed = remoteFeedInitialState;
      Object.assign(state.mediaPlaybackBlocked, blockedMediaPlaybackInitialState);
    },
    // Field-wise assignment lets Immer treat same-value snapshots as no-ops, avoiding re-renders.
    remoteMediaPlaybackBlockedStateChanged(
      state,
      action: PayloadAction<BlockedMediaPlaybackState>
    ) {
      Object.assign(state.mediaPlaybackBlocked, action.payload);
    },
    deviceProcessingStarted(state, action: PayloadAction<'audio' | 'video'>) {
      if (action.payload === 'audio') {
        state.publishingFeed.audioProcessing = true;
      } else {
        state.publishingFeed.videoProcessing = true;
      }
    },
    deviceProcessingStopped(state, action: PayloadAction<'audio' | 'video'>) {
      if (action.payload === 'audio') {
        state.publishingFeed.audioProcessing = false;
      } else {
        state.publishingFeed.videoProcessing = false;
      }
    },
    hdStreamToggled(state) {
      state.publishingFeed.hdVideo.enabled = !state.publishingFeed.hdVideo.enabled;
    },
    preserveAspectRatioToggled(state) {
      state.publishingFeed.preserveAspectRatio = !state.publishingFeed.preserveAspectRatio;
    },
    mirrorVideoToggled(state) {
      state.publishingFeed.mirrorVideo = !state.publishingFeed.mirrorVideo;
    },
    activeSpeakerReset(state) {
      state.activeSpeakerId = '';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(activeSpeakerChanged, (state, action) => {
        state.activeSpeakerId = action.payload.id;
      })
      .addCase(speakerStoppedTalking, (state, action) => {
        if (state.activeSpeakerId === action.payload.id) {
          state.activeSpeakerId = '';
        }
      })
      .addCase(phoneStreamDeleted, (state, action) => {
        const userId = action.payload;

        if (state.activeSpeakerId === userId) {
          state.activeSpeakerId = '';
        }

        delete state.broadcastingByUserId[userId];
      })
      .addCase(signalingBreakoutUserLeft, (state, action) => {
        const { userId } = action.payload;

        if (state.activeSpeakerId === userId) {
          state.activeSpeakerId = '';
        }

        delete state.broadcastingByUserId[userId];
      })
      .addCase(signalingUserLeft, (state, action) => {
        const { id: userId } = action.payload;

        if (state.activeSpeakerId === userId) {
          state.activeSpeakerId = '';
        }

        delete state.broadcastingByUserId[userId];
      })
      .addCase(authorizeUser.fulfilled, (state, action) => {
        const { media, settings } = action.payload;
        state.publishingFeed.hdVideo = {
          enabled: media.hdVideo,
          bitrate: media.hdVideoQuality.bitrate,
        };
        if (settings.videoFitModeEnabled && !hasStoredPreserveAspectRatio()) {
          state.publishingFeed.preserveAspectRatio = true;
        }
      })
      .addCase(publisherJoined, (state) => {
        state.publishingFeed.publisherJoined = true;
      })
      .addCase(publisherJoinedWithoutBroadcast, (state) => {
        state.publishingFeed.publisherJoined = true;
      })
      .addCase(signalingAspectRatioAdjusted, (state, action) => {
        state.preserveAspectRatioByUserId[action.payload.id] = action.payload.keep;
      })
      .addCase(signalingRoomJoined, (state, action) => {
        const preserveAspectRatioConfig: Record<UserId, boolean> = {};

        for (const user of action.payload.users) {
          preserveAspectRatioConfig[user.id] = user.keepAspectRatio;
        }

        state.preserveAspectRatioByUserId = {
          ...state.preserveAspectRatioByUserId,
          ...preserveAspectRatioConfig,
        };
      })
      .addCase(signalingUserJoined, (state, action) => {
        state.preserveAspectRatioByUserId[action.payload.id] = action.payload.keepAspectRatio;
      });
  },
});

export const {
  generalDataUpdated,
  localFeedUpdated,
  localFeedDisconnected,
  remoteFeedConnected,
  remoteFeedUpdated,
  deviceProcessingStarted,
  deviceProcessingStopped,
  hdStreamToggled,
  preserveAspectRatioToggled,
  mirrorVideoToggled,
  remoteFeedDisconnected,
  remoteMediaPlaybackBlockedStateChanged,
  activeSpeakerReset,
} = streamingSlice.actions;

export const selectLocalFeedStatus = (state: RootState) => state.streaming.publishingFeed.status;
export const selectLocalFeedStreams = (state: RootState) => state.streaming.publishingFeed.streams;
export const selectLocalScreenshareStreams = (state: RootState) =>
  state.streaming.screensharingFeed.streams;
export const selectLocalFeedId = (state: RootState) => state.streaming.publishingFeed.feedId;
export const selectLocalScreenshareFeedId = (state: RootState) =>
  state.streaming.screensharingFeed.feedId;

export const selectLocalStream = (
  state: RootState,
  kind: MediaStreamType,
  mode: 'media' | 'screenshare'
) =>
  state.streaming[mode === 'media' ? 'publishingFeed' : 'screensharingFeed'].streams.find(
    (stream) => stream.kind === kind
  );

export const selectFeedIdByUser = (state: RootState, userId: UserId) =>
  state.streaming.feedIdByUserId[userId];

export const selectRemoteStreams = createSelector(
  [
    (state: RootState) => state.streaming.remoteFeed.streamsByFeedId,
    selectFeedIdByUser,
    (state: RootState, userId: UserId, requestedFeedId?: FeedId) => requestedFeedId,
  ],
  (streamsByFeedId, feedId, requestedFeedId) => streamsByFeedId[requestedFeedId || feedId]
);

export const selectRemoteFeedByMid = (state: RootState, mid: string) =>
  state.streaming.remoteFeed.feedIdByMid[mid];

export const selectRemoteStreamByMid = (state: RootState, mid: string) => {
  const feedId = selectRemoteFeedByMid(state, mid);

  if (!feedId) {
    return undefined;
  }

  return state.streaming.remoteFeed.streamsByFeedId[feedId].find((stream) => stream.mid === mid);
};

export const selectRemoteStreamsByFeedId = (state: RootState, feedId?: FeedId) => {
  if (!feedId) {
    return undefined;
  }

  return state.streaming.remoteFeed.streamsByFeedId[feedId];
};

export const selectVideoMidByFeedId = (state: RootState, feedId?: FeedId) =>
  selectRemoteStreamsByFeedId(state, feedId)?.find((stream) => stream.kind === 'video')?.mid;

export const selectLocalVideoBroadcasting = (state: RootState) =>
  state.streaming.publishingFeed.videoBroadcasting;

export const selectLocalPublisherJoined = (state: RootState) =>
  state.streaming.publishingFeed.publisherJoined;

export const selectLocalScreenshareBroadcasting = (state: RootState) =>
  state.streaming.screensharingFeed.videoBroadcasting;
export const selectLocalAudioBroadcasting = (state: RootState) =>
  state.streaming.publishingFeed.audioBroadcasting;
export const selectLocalScreenshareAudioBroadcasting = (state: RootState) =>
  state.streaming.screensharingFeed.audioBroadcasting;

// Used by the stats orchestrator to set log cadence: audio-only broadcasters
// (e.g. dynamic-permission grants) should sample at the broadcaster rate, not listener rate.
export const selectLocalBroadcasting = (state: RootState) =>
  selectLocalAudioBroadcasting(state) || selectLocalVideoBroadcasting(state);

export const selectLocalVideoEnabled = (state: RootState) =>
  state.streaming.publishingFeed.videoEnabled;
export const selectLocalAudioEnabled = (state: RootState) =>
  state.streaming.publishingFeed.audioEnabled;

export const selectHDVideoSettings = (state: RootState) => state.streaming.publishingFeed.hdVideo;

export const selectPreserveAspectRatio = (state: RootState) =>
  state.streaming.publishingFeed.preserveAspectRatio;

export const selectMirrorVideo = (state: RootState) => state.streaming.publishingFeed.mirrorVideo;

export const selectPreserveAspectRatioByUserId = (state: RootState, userId: UserId) =>
  !!state.streaming.preserveAspectRatioByUserId[userId];

export const selectUserIdByFeed = (state: RootState, feedId: FeedId) =>
  state.streaming.userIdByFeedId[feedId] ?? state.streaming.userIdByScreensharingFeedId[feedId];

export const selectRemoteBroadcastingState = (
  state: RootState,
  userId: UserId,
  type: MediaStreamType
) => state.streaming.broadcastingByUserId[userId]?.[type];

export const selectRemoteUserBroadcastingState = (state: RootState, userId: UserId) =>
  state.streaming.broadcastingByUserId[userId];

export const selectAllBroadcasters = (state: RootState) => state.streaming.broadcastingByUserId;

export const selectRemoteScreensharingState = (
  state: RootState,
  userId: UserId,
  type: MediaStreamType
) => state.streaming.screensharingByUserId[userId]?.[type];

export const selectIsActiveSpeaker = (state: RootState, userId: UserId) =>
  state.streaming.activeSpeakerId === userId;

export const selectBlockedMediaPlayback = (state: RootState) =>
  state.streaming.mediaPlaybackBlocked;

export const selectIsRemoteMediaPlaybackBlocked = (state: RootState) =>
  state.streaming.mediaPlaybackBlocked.audio ||
  state.streaming.mediaPlaybackBlocked.mainDocumentVideo ||
  state.streaming.mediaPlaybackBlocked.pipDocumentVideo;

export default streamingSlice.reducer;
